var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/extensions/ImageExtension.ts
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-hr.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import "@adornis/chemistry/elements/components/x-loader.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/dialog/x-native-dialog.js";
import "@adornis/file-utils/client/x-image.js";
import "@adornis/fonts/fonts.js";
import "@adornis/forms/x-checkbox.js";
import "@adornis/forms/x-input.js";
import "@adornis/popover/x-dropdown-search-selection.js";
import "@adornis/popover/x-dropdown-selection.js";
import { goTo } from "@adornis/router/client/open-href.js";
import { html, nothing } from "lit";
import { customElement } from "lit/decorators.js";
import { BuildifyImage } from "../../db/BuildifyFile.js";
import { BaseImage } from "../../db/extensions/file/BaseImage.js";
import "../components/x-buildify-aspect-ratio-picker.js";
import "../components/x-buildify-border-radius-picker.js";
import "../components/x-buildify-file-selection.js";
import "../components/x-buildify-permission-selection.js";
import "../components/x-buildify-spacing-picker.js";
import "../components/x-infobox.js";
import { ExtensionBoilerplate } from "./ContentExtensionBoilerplate.js";
var ImageExtension = {
  class: BaseImage,
  toolbar: { icon: "image", text: { default: "Bild" } },
  render(instance, { defaultValues, isSubelement, dropzoneBefore, dropzoneAfter }) {
    return xComponents(html`
      <x-image-extension
        .content=${instance}
        .defaultValues=${defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></x-image-extension>
    `);
  },
  editor({ content, contentController, controllerBaseKeyPath }) {
    return html`
      <x-flex space="md">
        <x-input
          ${contentController.field(...controllerBaseKeyPath, "altTag")}
          placeholder="Beschreibung (SEO)"
        ></x-input>
        <x-buildify-spacing-picker
          ${contentController.field(...controllerBaseKeyPath, "width")}
          placeholder="Breite"
        ></x-buildify-spacing-picker>
        <x-buildify-spacing-picker
          ${contentController.field(...controllerBaseKeyPath, "height")}
          placeholder="Höhe"
        ></x-buildify-spacing-picker>
        <x-infobox>
          Wenn ein Seitenverhältnis angegeben wird, sollte nur mit einer Größe von Breite oder Höhe gearbeitet werden.
        </x-infobox>
        <x-buildify-aspect-ratio-picker
          ${contentController.field(...controllerBaseKeyPath, "aspectRatio")}
        ></x-buildify-aspect-ratio-picker>
        <x-buildify-border-radius-picker
          select
          ${contentController.field(...controllerBaseKeyPath, "borderRadius")}
          placeholder="Radius der Ecken"
        ></x-buildify-border-radius-picker>
        <x-dropdown-selection
          .selectables=${["cover", "contain", "width"]}
          placeholder="Anzeige-Modus"
          ${contentController.field(...controllerBaseKeyPath, "viewMode")}
          .renderString=${(viewMode) => {
      switch (viewMode) {
        case "cover":
          return "Platz ausf\xFCllend";
        case "contain":
          return "Vollst\xE4ndiges Bild in verf\xFCgbarem Platz";
        case "width":
          return "Vollst\xE4ndiges Bild auf voller Breite";
        default:
          return "";
      }
    }}
        ></x-dropdown-selection>
        <x-checkbox
          ${contentController.field(...controllerBaseKeyPath, "viewDownloadButton")}
          .label=${"Download Button anzeigen"}
        ></x-checkbox>
        <x-buildify-file-selection
          image
          ${contentController.field(...controllerBaseKeyPath, "fileID")}
        ></x-buildify-file-selection>
      </x-flex>
    `;
  },
  group: "Medien"
};
var XImageExtension = class extends ExtensionBoilerplate {
  _content(content) {
    return xComponents(
      html`
        <x-image
          .alt=${content.altTag}
          disabled
          mode=${content.viewMode ?? this.defaultMode}
          .fileEntity=${this.fileClass}
          .value=${content.fileID}
          ${css({
        width: content.width ? `min(100%, ${content.width})` : "100%",
        height: content.viewMode !== "width" ? "100%" : "unset",
        aspectRatio: content.viewMode !== "width" ? content.aspectRatio ?? "" : "unset",
        ...content.borderRadius ? { borderRadius: content.borderRadius, overflow: "hidden" } : {}
      })}
        ></x-image>
        ${content.viewDownloadButton ? this.__downloadButton(content.fileID) : nothing}
      `
    );
  }
  __downloadButton(fileID) {
    return html`
      <x-icon
        ${css({
      position: "absolute",
      right: "16px",
      bottom: "16px",
      zIndex: "9",
      background: this.colors.accent,
      color: "#fff",
      cursor: "pointer",
      padding: "4px",
      borderRadius: this.sizes.borderRadius
    })}
        @click=${() => {
      this._onDownload(fileID);
    }}
      >
        download
      </x-icon>
    `;
  }
  get defaultMode() {
    return "cover";
  }
  get fileClass() {
    return BuildifyImage;
  }
  _onDownload(fileID) {
    goTo(this.fileClass.getDownloadLink(fileID), { target: "_blank" });
  }
  styles() {
    const width = (() => {
      const defaultWidth = "100%";
      const w = this.defaultValue("width", { global: defaultWidth });
      if (typeof w === "string") {
        return `min(100%, ${w})`;
      } else {
        return defaultWidth;
      }
    })();
    const height = (() => {
      const defaultHeight = "auto";
      const h = this.defaultValue("height", { global: defaultHeight });
      if (typeof h !== "string" || h === defaultHeight) {
        return defaultHeight;
      } else {
        return `min(100%, ${h})`;
      }
    })();
    return [
      ...super.styles(),
      {
        ":host": {
          width,
          height
        }
      }
    ];
  }
};
XImageExtension = __decorateClass([
  customElement("x-image-extension")
], XImageExtension);
export {
  ImageExtension,
  XImageExtension
};
